import { supabase } from '../lib/supabaseClient';

// =====================
// USER API
// =====================

export const userAPI = {
  // Get all users with pagination and search
  getAllUsers: async (token, page = 1, search = '') => {
    const limit = 10;
    const offset = (page - 1) * limit;

    let query = supabase
      .from('profiles')
      .select('*', { count: 'exact' });

    // Add search filter
    if (search && search.trim()) {
      query = query.or(`username.ilike.%${search}%,email.ilike.%${search}%`);
    }

    // Add pagination
    query = query
      .range(offset, offset + limit - 1)
      .order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw new Error(error.message);

    const totalPages = Math.ceil(count / limit);

    return {
      data: data, // Pure Supabase format
      totalPages,
      currentPage: page,
    };
  },

  setUserRole: async (userId, role) => {
    const { data, error } = await supabase.rpc('admin_set_user_role', {
      p_user_id: userId,
      p_role: role,
    });

    if (error) throw new Error(error.message);
    if (data && !data.success) {
      throw new Error(data.error || 'Failed to update role');
    }

    return data;
  },

  // Delete user
  deleteUser: async (token, userId) => {
    // Use admin function to delete user and all related data
    const { data, error } = await supabase.rpc('admin_delete_user', {
      target_user_id: userId
    });

    if (error) throw new Error(error.message);
    
    if (data && !data.success) {
      throw new Error(data.error || 'Failed to delete user');
    }

    return { message: 'User deleted successfully' };
  },
};

// =====================
// POSTS API (Locations)
// =====================

export const postsAPI = {
  // Get all posts with pagination and search
  getAllPosts: async (token, page = 1, search = '') => {
    const limit = 10;
    const offset = (page - 1) * limit;

    let query = supabase
      .from('locations')
      .select(`
        *,
        profile:posted_by (
          id,
          username,
          email
        )
      `, { count: 'exact' });

    // Add search filter
    if (search && search.trim()) {
      query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
    }

    // Add pagination
    query = query
      .range(offset, offset + limit - 1)
      .order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw new Error(error.message);

    const totalPages = Math.ceil(count / limit);

    return {
      data: data, // Pure Supabase format
      totalPages,
      currentPage: page,
    };
  },

  // Get reported posts
  getReportedPosts: async () => {
    const { data, error } = await supabase
      .from('locations')
      .select(`
        *,
        profile:posted_by (
          id,
          username,
          email
        )
      `)
      .eq('is_reported', true)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);

    return data; // Pure Supabase format
  },

  // Create post
  createPost: async (token, postData) => {
    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
      console.error('Session error:', sessionError);
      throw new Error('Authentication failed');
    }

    // Insert new location
    const { data, error } = await supabase
      .from('locations')
      .insert([{
        title: postData.title,
        description: postData.description,
        category: postData.category,
        latitude: parseFloat(postData.location.latitude),
        longitude: parseFloat(postData.location.longitude),
        posted_by: session.user.id,
        username: postData.username,
        isverified: Boolean(postData.isverified),
        is_reported: false,
        report_reasons: [],
      }])
      .select()
      .single();

    if (error) {
      console.error('Insert error:', error);
      throw new Error(error.message);
    }

    return data; // Pure Supabase format
  },

  // Get a single post by ID
  getPostById: async (token, postId) => {
    const { data, error } = await supabase
      .from('locations')
      .select(`
        *,
        profile:profiles!locations_posted_by_fkey (
          id,
          username,
          email
        )
      `)
      .eq('id', postId)
      .single();

    if (error) throw new Error(error.message);
    return data;
  },

  // Update a post
  updatePost: async (token, postId, updates) => {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) throw new Error('Authentication failed');

    const { data, error } = await supabase
      .from('locations')
      .update({
        title: updates.title,
        description: updates.description,
        updated_at: new Date().toISOString(),
      })
      .eq('id', postId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  },

  // Delete a post
  deletePost: async (token, postId) => {
    const { error } = await supabase
      .from('locations')
      .delete()
      .eq('id', postId);

    if (error) throw new Error(error.message);
    return { success: true };
  },
};

// =====================
// REFERRALS API
// =====================

export const referralsAPI = {
  // Get all referrals with user details
  getAllReferrals: async () => {
    const { data, error } = await supabase.rpc('admin_get_all_referrals');
    
    if (error) throw new Error(error.message);
    return data || [];
  },

  // Get referral stats summary
  getReferralStats: async () => {
    // Get total referrals count
    const { count: totalReferrals, error: countError } = await supabase
      .from('referrals')
      .select('*', { count: 'exact', head: true });

    if (countError) throw new Error(countError.message);

    // Get top referrers
    const { data: topReferrers, error: topError } = await supabase
      .from('profiles')
      .select('id, username, email, referral_count')
      .gt('referral_count', 0)
      .order('referral_count', { ascending: false })
      .limit(10);

    if (topError) throw new Error(topError.message);

    // Get users with referrals (referrers)
    const { count: uniqueReferrers, error: referrersError } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .gt('referral_count', 0);

    if (referrersError) throw new Error(referrersError.message);

    // Get referred users count
    const { count: referredUsers, error: referredError } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .not('referred_by', 'is', null);

    if (referredError) throw new Error(referredError.message);

    return {
      totalReferrals: totalReferrals || 0,
      uniqueReferrers: uniqueReferrers || 0,
      referredUsers: referredUsers || 0,
      topReferrers: topReferrers || [],
    };
  },

  // Get all referrals with user details
  searchReferrals: async (searchTerm = '') => {
    // Get referrals
    const { data: referrals, error: refError } = await supabase
      .from('referrals')
      .select('*')
      .order('created_at', { ascending: false });

    if (refError) throw new Error(refError.message);
    if (!referrals || referrals.length === 0) return [];

    // Get all unique user IDs
    const userIds = [...new Set([
      ...referrals.map(r => r.referrer_id),
      ...referrals.map(r => r.referred_id)
    ])].filter(Boolean);

    // Get user profiles
    const { data: profiles, error: profError } = await supabase
      .from('profiles')
      .select('id, username, email, referral_count')
      .in('id', userIds);

    if (profError) throw new Error(profError.message);

    // Create lookup map
    const profileMap = {};
    (profiles || []).forEach(p => {
      profileMap[p.id] = p;
    });

    // Build result with user info
    let result = referrals.map(r => ({
      id: r.id,
      created_at: r.created_at,
      status: r.status,
      paid: r.paid || false,
      paid_at: r.paid_at,
      referrer: profileMap[r.referrer_id] || null,
      referred: profileMap[r.referred_id] || null,
    }));

    // Filter by search term if provided
    const term = (searchTerm || '').toLowerCase().trim();
    if (term) {
      result = result.filter(r => 
        r.referrer?.username?.toLowerCase().includes(term) ||
        r.referrer?.email?.toLowerCase().includes(term) ||
        r.referred?.username?.toLowerCase().includes(term) ||
        r.referred?.email?.toLowerCase().includes(term)
      );
    }

    return result;
  },

  // Mark a referral as paid
  markAsPaid: async (referralId) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('referrals')
      .update({ 
        paid: true, 
        paid_at: new Date().toISOString(),
        paid_by: user?.id 
      })
      .eq('id', referralId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  },

  // Mark a referral as unpaid (undo)
  markAsUnpaid: async (referralId) => {
    const { data, error } = await supabase
      .from('referrals')
      .update({ 
        paid: false, 
        paid_at: null,
        paid_by: null 
      })
      .eq('id', referralId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  },
};

// Export combined API object to match the old structure
export const api = {
  users: userAPI,
  posts: postsAPI,
  referrals: referralsAPI,
};

export default api;
